import type { StateContext } from '@ngxs/store';
import { type DynamicFlatNode, type IChannel, SmartTimeWindow } from 'commons';
import type { MultiViewStateModel } from '../MultiViewStateModel';

/**
 * Calculates the time boundaries for a given view index and entity.
 *
 * @param ctx - The state context of the MultiViewStateModel.
 * @param currentViewIndex - The index of the current view.
 * @param entity - The entity containing the first and latest timestamps.
 * @returns An object containing the start time, end time, and smart time resolution.
 */
export function getTimeBoundaries(ctx: StateContext<MultiViewStateModel>, currentViewIndex: number, { entity }: DynamicFlatNode<IChannel>) {
  const view = ctx.getState().views[currentViewIndex];
  const activeZoomState =
    view.zoomState.filter((zs) => !zs.dataZoomId.includes('vertical')).find((zs) => zs.dataZoomId === view.lastZoomedId) ?? view.zoomState[0];
  const { startValue, endValue } = activeZoomState;

  const latestTimestamp = new Date(entity.latestTimestamp);
  const firstTimestamp = new Date(entity.firstTimestamp);

  // if both is defined, calculate the difference
  const differenceZoomed = (startValue ?? endValue) ? Number(endValue) - Number(startValue) : undefined;
  const timeDifference = latestTimestamp.getTime() - firstTimestamp.getTime();
  const smartTime = new SmartTimeWindow(differenceZoomed ?? timeDifference).getResolution();

  return {
    startTime: firstTimestamp,
    endTime: latestTimestamp,
    ...smartTime,
  };
}
